// ============================================================================
// FILE 1 (new): src/client/common/elementHover/index.tsx
// ----------------------------------------------------------------------------
// Hover as an external store — same pattern as optimizedGantt/scrollPosStore.
// Placed in common/ because wbs rows are used by both the optimizedGantt and
// workloads scenes, and both currently thread hover through their contexts.
//
// Why: today hover lives in a useState inside Gantt, so every mouseEnter /
// mouseLeave over any bar or WBS row re-renders the entire Gantt tree
// (~258ms/pass, 10 passes in the last trace ≈ the pointer crossing 5 rows).
// With this store, a hover change re-renders ONLY the components whose
// `isHovered` boolean actually flips: the row + bar being left and the row +
// bar being entered — ≤4 components, ~1–2ms.
// ============================================================================

import React from "react";
import { OptimizedGantt } from "@planzone/dto";

type GanttElement = OptimizedGantt[number];
type Listener = () => void;

function createHoverStore() {
  let value: GanttElement | null = null;
  const listeners = new Set<Listener>();
  const notify = () => listeners.forEach((l) => l());

  return {
    get: () => value,
    // Same semantics as the old useHover: only set when the uuid actually
    // changes; only clear when the leaving element IS the hovered one.
    hoverElement: (element: GanttElement) => {
      if (value !== null && value.uuid === element.uuid) return;
      value = element;
      notify();
    },
    leaveElement: (element: GanttElement) => {
      if (value === null || value.uuid !== element.uuid) return;
      value = null;
      notify();
    },
    subscribe: (l: Listener) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

type HoverStore = ReturnType<typeof createHoverStore>;

const HoverContext = React.createContext<HoverStore | null>(null);
const HoverProvider = HoverContext.Provider;

function useHoverStore(): HoverStore {
  const store = React.useContext(HoverContext);
  if (!store) throw new Error("HoverProvider is missing");
  return store;
}

/**
 * Per-row subscription. The snapshot is a boolean, so React only re-renders
 * this subscriber when ITS boolean flips — a hover change wakes exactly the
 * rows/bars entering and leaving, nothing else.
 */
function useIsElementHovered(uuid: string): boolean {
  const store = useHoverStore();
  return React.useSyncExternalStore(
    store.subscribe,
    () => store.get()?.uuid === uuid,
    () => false,
  );
}

/**
 * Full-object subscription — for the few consumers that need the element
 * itself (e.g. constraintsOverlay, detailsPopup). Re-renders on every hover
 * change, so keep these components small.
 */
function useHoveredElement(): GanttElement | null {
  const store = useHoverStore();
  return React.useSyncExternalStore(store.subscribe, store.get, () => null);
}

/**
 * Setters only — NO subscription. Rows/bars that merely report hover
 * (onMouseEnter/onMouseLeave) never re-render because of hover.
 */
function useHoverActions() {
  const store = useHoverStore();
  return React.useMemo(
    () => ({
      hoverElement: store.hoverElement,
      leaveElement: store.leaveElement,
    }),
    [store],
  );
}

export {
  createHoverStore,
  HoverProvider,
  useIsElementHovered,
  useHoveredElement,
  useHoverActions,
};

export type { HoverStore };
